import { ApolloError, DocumentNode, gql } from '@apollo/client';
import { useMemo } from 'react';

import { SMSSender } from 'modules/graphql/types';
import { useGraphql } from 'utils/hooks/useGraphql';
import noop from 'utils/noop';
import { isDefined } from 'utils/tsHelpers';

export type FetchSMSSendersByMessageTypeQueryRequest = {
  id: number;
};

type FetchSMSSendersByMessageTypeQueryResponse = {
  smsSendersLimitedByMessageType: SMSSender[];
};

export type UseSMSSendersByMessageTypeResponse = {
  errorMessage?: ApolloError;
  hasError: boolean;
  isLoading: boolean;
  refetch: (variables?: Partial<FetchSMSSendersByMessageTypeQueryRequest>) => void;
  results: SMSSender[];
};

export const fetchSMSSendersByMessageType = gql`
  query FetchSMSSendersByMessageType($id: Long!) {
    smsSendersLimitedByMessageType(id: $id) {
      id
      name
      phoneNumber
      type
    }
  }
`;

export function useSMSSendersByMessageType(
  options: Record<string, any>,
  query: DocumentNode = fetchSMSSendersByMessageType, // eslint-disable-line
): UseSMSSendersByMessageTypeResponse {
  const queryOptions = useMemo(
    () => ({
      notifyOnNetworkStatusChange: true,
      ...options,
    }),
    [options],
  );
  const {
    data: fetchSMSSendersByMessageTypeQueryData,
    error: hasError = false,
    errorMessage,
    isLoading,
    refetch,
  } = useGraphql(query, queryOptions);

  // protect against the undefined response while the query is running
  if (isDefined<FetchSMSSendersByMessageTypeQueryResponse>(fetchSMSSendersByMessageTypeQueryData)) {
    const results = fetchSMSSendersByMessageTypeQueryData.smsSendersLimitedByMessageType || [];

    return {
      errorMessage,
      hasError,
      isLoading,
      refetch,
      results,
    };
  }

  return {
    errorMessage,
    hasError,
    isLoading,
    refetch: noop,
    results: [],
  };
}
